import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import type { TicketCategory } from '../../operatorTickets/types';
import { formatEuroFromCents } from '../../operatorTickets/utils';

import { formatFirstRenewalDate } from './date';

interface IPriceCalculationProps {
	operatorName?: string;
	ticketName: string;
	priceInCents: number;
	category: TicketCategory;
	/** Subscription only — number of billing periods (months). */
	periodCount?: number;
	/** ISO start date — used to derive the first renewal date for subscriptions. */
	startDate: string;
}

export const PriceCalculation: FC<IPriceCalculationProps> = ({
	operatorName,
	ticketName,
	priceInCents,
	category,
	periodCount,
	startDate,
}) => {
	const { t } = useTranslation('ticketshop');
	const formattedPrice = formatEuroFromCents(priceInCents);
	const isSubscription = category === 'subscription';

	return (
		<Stack gap={1} sx={{ width: '100%' }}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				gap={1}
			>
				<Typography variant="b2" color="primary.main">
					{t('payment.price_line', {
						operator: operatorName ?? '',
						ticket: ticketName,
					})}
				</Typography>
				<Typography variant="b2" color="primary.main" noWrap>
					{formattedPrice}
				</Typography>
			</Stack>

			{/* TODO(ticketshop): VAT rate is static copy — if needed, wire the backend-provided rate when available. */}
			<Typography variant="b2" color="primary.main">
				{t('payment.vat_note')}
			</Typography>

			{isSubscription && (
				<Stack gap={0.25}>
					<Typography variant="l1" color="solutions.600">
						{t('payment.subscription_recurring', {
							price: formattedPrice,
							count: periodCount ?? 1,
						})}
					</Typography>
					<Typography variant="l1" color="solutions.600">
						{t('payment.subscription_first_renewal', {
							date: formatFirstRenewalDate(startDate),
						})}
					</Typography>
				</Stack>
			)}
		</Stack>
	);
};
